import type { BusinessMetadata } from "./business-metadata";
import type { Language } from "@/domain/localization";

export type Localized = Record<Language, string>;

export type LoadStatus = "idle" | "loading" | "ready" | "empty" | "error";
export type EntityStatus = "active" | "inactive";
export type OnboardingStatus = "analyzing" | "ready" | "not_relevant" | "failed";

export type Branding = {
  primaryColor: string;
  secondaryColor: string;
  fontFamily: string;
  cornerRadius: string;
  buttonStyle: string;
  heroStyle: string;
};

export type NotificationPreferences = {
  leadEmail?: string | undefined;
  emailAlerts: boolean;
  dailyDigest: boolean;
  handoffAlerts: boolean;
};

export type EditLock = {
  actorId: string;
  leaseId: string;
  acquiredAt: Date;
  heartbeatAt: Date;
  expiresAt: Date;
};

export type CommercialState = { type: "standard" | "design_partner"; accessStatus: "inactive" | "active" | "suspended" };
export type CustomerMember = { uid: string; role: "manager"; status: "active" | "inactive"; email: string | null; createdAt: Date; updatedAt: Date };

export type CustomerAccount = {
  commercial?: CommercialState | null | undefined;
  management?: { currentManagerUid?: string | null | undefined; establishedAt?: Date | null | undefined } | null | undefined;
  id: string;
  normalizedDomain: string;
  websiteUrl: string;
  onboardingStatus: OnboardingStatus;
  qottaFit: boolean | null;
  qottaFitReason: string;
  revision: number;
  editLock: EditLock | null;
  notificationPreferences: NotificationPreferences;
  status: EntityStatus;
  isDemo: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type BusinessProfile = BusinessMetadata & {
  primaryLanguage?: Language | undefined;
  name: Localized;
  description: Localized;
  website: string;
  vertical: Localized;
  contact: { email: string; phone: string };
  serviceAreas: Localized[];
  currency: string;
  timezone: string;
  branding: Branding;
  publicSlug: string;
  defaultScenarioId: string;
  updatedAt: Date;
};

export type PricingDisplay = "range" | "from" | "fixed" | "hidden";
export type PricingSourceType = "company_website" | "market_research" | "estimated";
export type PricingConfidence = "high" | "medium" | "low";

export type PricingFactor = {
  id: string;
  label: Localized;
  kind: "fixed" | "per-unit" | "multiplier";
  amount?: number | undefined;
  low?: number | undefined;
  high?: number | undefined;
  multiplier?: number | undefined;
  answerId?: string | undefined;
  answerValues?: Array<string | number> | undefined;
  conditionAnswerId?: string | undefined;
  conditionAnswerValues?: Array<string | number> | undefined;
};

export type PricingMetadata = {
  sourceType: PricingSourceType;
  confidence: PricingConfidence;
  conditions: Localized[];
  exceptions: Localized[];
  notes: Localized;
};
export type QuotePricing = { availability: "quote-required"; currency: string; factors: PricingFactor[]; display?: undefined; low?: undefined; high?: undefined; fixed?: undefined };
export type NumericPricing = {
  currency: string; display: PricingDisplay; low: number; high: number;
  fixed?: number | undefined; factors: PricingFactor[];
};
export function isNumericPricing<T extends NumericPricing | QuotePricing>(pricing: T): pricing is T & NumericPricing {
  return !("availability" in pricing);
}
export type EmbeddedPricing = PricingMetadata & (NumericPricing | QuotePricing);

export type Product = {
  id: string;
  name: Localized;
  description: Localized;
  status: EntityStatus;
  sourceIds: string[];
  sortOrder: number;
  pricing: EmbeddedPricing;
};

export type Service = Product;

export type KnowledgeKind = "faq" | "policy" | "process" | "regulation" | "general";

export type KnowledgeItem = {
  id: string;
  type: KnowledgeKind;
  title: Localized;
  body: Localized;
  sourceIds: string[];
  sortOrder: number;
};

export type Source = {
  id: string;
  kind: "website" | "file" | "text";
  name: string;
  url?: string | undefined;
  mimeType?: string | undefined;
  bytes?: number | undefined;
  contentHash?: string | undefined;
  status: "processing" | "ready" | "failed";
  summary: Localized;
  updatedAt: Date;
};

export type DiscoveryOption = {
  id: string;
  label: Localized;
  hint?: Localized | undefined;
  icon?: string | undefined;
};

export type DiscoveryQuestion =
  | { id: string; kind: "cards" | "chips" | "multi"; title: Localized; help?: Localized | undefined; options: DiscoveryOption[]; required?: boolean | undefined }
  | { id: string; kind: "slider"; title: Localized; help?: Localized | undefined; min: number; max: number; required?: boolean | undefined };

export type ScenarioStep = { id: string; title: Localized; questions: DiscoveryQuestion[] };

export type Scenario = {
  id: string;
  name: Localized;
  description: Localized;
  goal: Localized;
  status: EntityStatus;
  productIds: string[];
  serviceIds: string[];
  pricingDisplay: PricingDisplay;
  handoffPolicy: "request" | "high-value" | "low-confidence";
  steps: ScenarioStep[];
};

export type BusinessContext = { content: string; basedOnRevision: number; updatedAt: Date };

export type BusinessTruth = {
  account: CustomerAccount;
  business: BusinessProfile;
  products: Product[];
  services: Service[];
  knowledge: KnowledgeItem[];
  scenarios: Scenario[];
  sources: Source[];
  context: BusinessContext;
};

export type DiscoveryAnswerValue = string | number | string[];
export type DiscoveryAnswers = Record<string, DiscoveryAnswerValue | undefined>;
export type DiscoveryMode = "live" | "demo" | "simulator";
export type DiscoverySessionContext = {
  mode: DiscoveryMode;
  sourceUrl: string;
  sourcePath: string;
  sourceTitle: string;
  sessionId: string;
};
export type DiscoveryStage = "intro" | "questions" | "result" | "contact" | "sent";
export type DiscoveryStatus = "in_progress" | "completed" | "abandoned";

export type DiscoveryEstimate = {
  low: number;
  high: number;
  currency: string;
  confidence: number;
  includes: Localized[];
};

export type DiscoveryResult = {
  recommendedProductIds: string[];
  recommendedServiceIds: string[];
  summary: Localized;
  assumptions: Localized[];
  generatedAt: Date;
} & ({ estimate: DiscoveryEstimate; pricingStatus?: never } |
     { estimate: null; pricingStatus: "quote-required" | "hidden" });

export type Discovery = {
  /** Active intentional estimate revision; removed when its result is saved. */
  revisionId?: string;
  schemaVersion?: 2;
  modelId?: string;
  visitorNotes?: string;
  presentedQuestions?: Array<{ questionId: string; language: string }>;
  id: string;
  sessionId: string;
  context: DiscoverySessionContext;
  visitorUid?: string | undefined;
  authenticatedUserId?: string | undefined;
  scenarioId: string;
  businessRevision: number;
  status: DiscoveryStatus;
  stage: DiscoveryStage;
  currentStepId: string | null;
  currentQuestionId: string | null;
  questionIndex: number;
  answers: DiscoveryAnswers;
  result: DiscoveryResult | null;
  leadId?: string | undefined;
  startedAt: Date;
  firstAnswerAt: Date | null;
  completedAt: Date | null;
  contactRequestedAt: Date | null;
  updatedAt: Date;
  expiresAt: Date;
};

export const leadStatuses = ["new", "contacted", "interested", "quoted", "won", "closed"] as const;
export type LeadStatus = (typeof leadStatuses)[number];

export type Lead = {
  leadOrigin?: "manual" | "discovery";
  displayedEstimate?: import("@/discovery/displayed-estimate").DisplayedEstimate;
  id: string;
  /** Copied from Discovery.context.mode. Simulator is retained only for historical records. */
  mode: DiscoveryMode;
  discoveryId: string;
  sessionId?: string | undefined;
  contact: { name: string; phone: string; email: string; business?: string; website?: string };
  customerNotes?: string;
  requestedAction: "quote" | "expert";
  /** Link to the immutable audit record for the latest explicit status mutation. */
  statusActivityId?: string;
  status: LeadStatus;
  createdAt: Date;
  updatedAt: Date;
};

export type ActivityKind = "deployment" | "lead" | "onboarding" | "source";

export type ActivityLog = {
  id: string;
  kind: ActivityKind;
  actorId: string;
  summary: Localized;
  baseRevision: number | null;
  revision: number | null;
  changes: Array<{ entity: string; id: string; operation: "create" | "update" | "delete" }>;
  createdAt: Date;
};

export type ErrorRecord = {
  code: string;
  category: "data" | "auth" | "network" | "validation" | "ai" | "deployment" | "onboarding" | "unexpected";
  operation: string;
  message: string;
  severity: "warning" | "error";
  retryable: boolean;
  source: "client" | "firebase" | "llm-proxy";
  entity?: { type: string; id?: string | undefined } | undefined;
  tool?: string | undefined;
  correlationId: string;
  sanitizedDetails?: Record<string, unknown> | undefined;
  stack?: string | undefined;
  createdAt: Date;
};

export type DashboardDay = {
  id: string;
  date: Date;
  discoveries: number;
  started: number;
  results: number;
  leads: number;
  quoteRequests: number;
  pipelineValue: number;
  estimateTotal: number;
  estimateDurationSecondsTotal: number;
};
